import { Injectable, Component, OnInit } from "@angular/core";
import { AxonComponent } from "../../axon.component";
import { ChartRow, Chart } from "../../dto/dtos";
import { DashBoardService } from "../../services/dashboard/dashboard.service";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";

interface ChartDefinition {
    key: string;
    title: string;
    row: number;
}

@Injectable({
    providedIn: 'root'
})

@Component({
    selector: 'app-system-dashboard',
    templateUrl: './system-dashboard.component.html',
    styleUrls: ['./system-dashboard.component.scss'],
})
export class SystemDashboardComponent extends AxonComponent implements OnInit {

    charts!: Array< ChartRow >;
    
    // Define all 8 charts
    private chartDefinitions: ChartDefinition[] = [
        { key: 'submissions', title: 'Submissions for Today', row: 1 },
        { key: 'active-agents', title: 'Active Agents', row: 1 },
        { key: 'waiting-approval', title: 'Waiting Approval', row: 1 },
        { key: 'submission-queue', title: 'Submission Queue', row: 1 },
        { key: 'system-summary-today', title: 'System Summary - Today', row: 2 },
        { key: 'system-summary-7', title: 'System Summary - Last 7 Days', row: 2 },
        { key: 'agent-role-login-7', title: 'Agent Role Login - 7 Days', row: 3 },
        { key: 'agent-role-login-30', title: 'Agent Role Login - 30 Days', row: 3 }
    ];
    
    // Map to track chart objects by key for in-place updates
    private chartsByKey: Map<string, Chart> = new Map();
    
    constructor(private dashBoardService: DashBoardService) {
        super();
        this.charts = new Array();
    }

    ngOnInit(): void {
        this.loadCharts();
    }

    getCharts() {
        // Legacy method - now calls loadCharts
        this.loadCharts();
    }

    private loadCharts() {
        // Step 1: Create placeholder charts immediately
        this.createPlaceholderCharts();
        
        // Step 2: Fire all 8 chart requests in parallel
        const chartRequests = this.chartDefinitions.map(def =>
            this.dashBoardService.startAndPollChart(def.key).pipe(
                catchError(error => {
                    console.error(`Failed to load chart ${def.key}:`, error);
                    // Return error chart
                    return of({
                        success: false,
                        dataList: [this.createErrorChart(def)]
                    });
                })
            )
        );
        
        // Step 3: Subscribe to all requests - each will update its chart in-place
        forkJoin(chartRequests).subscribe(
            responses => {
                responses.forEach((resp, index) => {
                    const def = this.chartDefinitions[index];
                    if (resp.success && resp.dataList && resp.dataList.length > 0) {
                        const chart = resp.dataList[0];
                        this.updateChart(def.key, chart);
                    } else {
                        const errorChart = this.createErrorChart(def);
                        this.updateChart(def.key, errorChart);
                    }
                });
                
                this.logChartStatus();
            },
            error => {
                console.error('Error loading charts:', error);
            }
        );
    }

    /**
     * Create placeholder charts with "Loading" type so the grid renders immediately
     */
    private createPlaceholderCharts() {
        this.charts = new Array();
        this.chartsByKey.clear();
        
        for (const def of this.chartDefinitions) {
            const placeholderChart: Chart = {
                title: def.title,
                type: 'Loading',  // Triggers loading spinner in template
                row: def.row,
                data: [],
                comboData: [],
                columnNames: [],
                params: '',
                options: {}
            };
            
            this.chartsByKey.set(def.key, placeholderChart);
            this.addChartToRow(placeholderChart);
        }
    }

    /**
     * Updates an existing chart in-place or adds it
     */
    private updateChart(key: string, newChart: Chart) {
        const existingChart = this.chartsByKey.get(key);
        const chartToUse: Chart = {
            ...newChart,
            options: existingChart ? (existingChart as any).options || {} : {}
        };

        this.chartsByKey.set(key, chartToUse);

        if (existingChart) {
            const rowIndex = this.charts.findIndex(row => row.row === existingChart.row);
            if (rowIndex >= 0) {
                const chartIndex = this.charts[rowIndex].charts.findIndex(chart => chart.title === existingChart.title);
                if (chartIndex >= 0) {
                    this.charts[rowIndex].charts[chartIndex] = chartToUse;
                } else {
                    this.addChartToRow(chartToUse);
                }
            } else {
                this.addChartToRow(chartToUse);
            }
        } else {
            this.addChartToRow(chartToUse);
        }
    }

    /**
     * Adds a chart to the appropriate row in this.charts
     */
    private addChartToRow(chart: Chart) {
        const row = Number(chart.row);
        chart.row = row;
        
        let foundRow = false;
        for (const chartRow of this.charts) {
            if (chartRow.row === row) {
                const chartsInRow = chartRow.charts;
                chartsInRow.push(chart);
                foundRow = true;
            }
        }

        if (!foundRow) {
            const chartsInRow = new Array<Chart>();
            chartsInRow.push(chart);
            const chartRow: ChartRow = {
                row: row,
                charts: chartsInRow
            };
            this.charts.push(chartRow);
        }
    }

    /**
     * Creates an error chart when a query fails
     */
    private createErrorChart(def: ChartDefinition): Chart {
        return {
            title: def.title + ' (Unable to load)',
            type: 'Error',
            row: def.row,
            data: [],
            comboData: [],
            columnNames: [],
            params: '',
            options: {}
        };
    }

    /**
     * Logs final chart status for debugging
     */
    private logChartStatus() {
        for (const chartRow of this.charts) {
            console.log(`Row [${chartRow.row}] - Number of charts [${chartRow.charts.length}]`);
            for (const chart of chartRow.charts) {
                console.log(`  Chart [${chart.title}] - type [${chart.type}]`);
            }
        }
    }
}